import MenuItem from "@mui/material/MenuItem";
import { CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import { MoreVertIcon } from "../images/MoreVertIcon";
import { StyledMenu } from "./StyledMenu";
import { useNavigate, useParams } from "react-router-dom";
import { EyeIcon } from "../images/EyeIcon";
import { ExportDataIcon, StarsOutlineIcon, TrashIcon } from "../images";
import {
  ActivitiesStatus,
  AppRoutes,
  Integration,
  PlaybookPlanNames,
} from "../../types/enums";
import {
  campaignsApi,
  useGeneratePlaybookForMembersMutation,
  useGeneratePlaybookPDFMutation,
  useGetCampaignDetailsQuery,
  useGetCampaignSummaryQuery,
  useLazyGetPlaybookQuery,
  useRemoveMembersMutation,
} from "../../api";
import { toast } from "react-toastify";
import { CustomToast } from "./CustomToast";
import { getErrorMessage } from "../../utils/getErrorMessage";
import {
  setIsNotificationsOpen,
  setRemovedIds,
} from "../../features/companySlice";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { selectPermissions } from "../../features/permissionsSlice";
import { ReuseModal } from "./ReuseModal";

export const MemberMenu = ({
  member_id,
  isDisabled,
  playbookStatus = ActivitiesStatus.queued,
}: {
  member_id: string;
  isDisabled: boolean;
  playbookStatus?: ActivitiesStatus;
}) => {
  const { campaign_id, account_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    isAllowedEditGenerateReports,
    isAllowedViewPlaybooks,
    isAllowedExportPlaybooks,
  } = useAppSelector(selectPermissions);

  const [isMissingHG, setIsMissingHG] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [removeMembers, { isLoading }] = useRemoveMembersMutation();
  const [generatePlaybookForMembers, { isLoading: isGeneratingPlaybooks }] =
    useGeneratePlaybookForMembersMutation();
  const [getPlaybook, { isFetching: isPlaybookLoading }] =
    useLazyGetPlaybookQuery();
  const [generatePlaybookPDF, { isLoading: isGeneratingPlaybookPDF }] =
    useGeneratePlaybookPDFMutation();

  const { data: campaignDetails } = useGetCampaignDetailsQuery(
    {
      account_id: account_id!,
    },
    { skip: !account_id },
  );

  const { data: summary } = useGetCampaignSummaryQuery({
    campaign_id: campaign_id!,
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const exportPlaybook = () => {
    getPlaybook({ member_id })
      .unwrap()
      .then((topics) => {
        generatePlaybookPDF({ member_id, topics, campaign_id: campaign_id! })
          .unwrap()
          .then((blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${PlaybookPlanNames.playbook} for ${topics.lead_company} - ${topics.lead_name}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            handleClose();
          })
          .catch((err) => {
            toast.error(
              <CustomToast
                type={"error"}
                text={
                  getErrorMessage(err).globalError ||
                  "Something went wrong. Please try again"
                }
                title={`Failed to generate ${PlaybookPlanNames.playbook} PDF`}
              />,
            );
          });
      })
      .catch((err) =>
        toast.error(
          <CustomToast
            type="error"
            title="Something went wrong"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
          />,
        ),
      );
  };

  const removeMemberHandler = () => {
    if (!campaign_id || !account_id) return null;
    removeMembers({ campaign_id, account_id, member_ids: [member_id] })
      .unwrap()
      .then(() => {
        handleClose();
        dispatch(setRemovedIds([member_id]));
        dispatch(campaignsApi.util.invalidateTags(["role-members", "company"]));
      })
      .catch((err) =>
        toast.error(
          <CustomToast
            type="error"
            title="Something went wrong"
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
          />,
        ),
      );
  };

  const generatePlaybooksHandler = () => {
    if (campaign_id) {
      setIsMissingHG(false);
      generatePlaybookForMembers({
        campaign_id,
        ids: [member_id],
        include_all: false,
      })
        .unwrap()
        .then(() => {
          dispatch(setIsNotificationsOpen(true));
          handleClose();
        })
        .catch((err) =>
          toast.error(
            <CustomToast
              type="error"
              text={
                getErrorMessage(err).globalError ||
                "Something went wrong. Please try again"
              }
              title={`${PlaybookPlanNames.playbook} generation failed.`}
            />,
          ),
        );
    }
  };

  const list = useMemo(
    () => [
      {
        label: `Generate ${PlaybookPlanNames.playbook}`,
        icon: <StarsOutlineIcon color={"#1D3E70"} />,
        handler:
          campaignDetails?.has_hg_data ||
          !(
            !!summary?.crm && summary.crm.integration === Integration.lead_tools
          )
            ? generatePlaybooksHandler
            : () => setIsMissingHG(true),
        isLoading:
          playbookStatus === ActivitiesStatus.in_progress ||
          isGeneratingPlaybooks,
        isDisabled:
          playbookStatus === ActivitiesStatus.in_progress ||
          isGeneratingPlaybooks ||
          !isAllowedEditGenerateReports,
        isHide: playbookStatus === ActivitiesStatus.completed,
      },
      {
        label: `View ${PlaybookPlanNames.playbook}`,
        icon: <EyeIcon color={"#1D3E70"} />,
        handler: () =>
          navigate(`/${campaign_id}${AppRoutes.PLAYBOOK}/${member_id}`),
        isLoading: false,
        isDisabled: !isAllowedViewPlaybooks,
        isHide: playbookStatus !== ActivitiesStatus.completed,
      },
      {
        label: `Export ${PlaybookPlanNames.playbook} (PDF)`,
        icon: <ExportDataIcon />,
        handler: exportPlaybook,
        isLoading: isGeneratingPlaybookPDF || isPlaybookLoading,
        isDisabled:
          isGeneratingPlaybookPDF ||
          isPlaybookLoading ||
          !isAllowedExportPlaybooks,
        isHide: playbookStatus !== ActivitiesStatus.completed,
      },
      {
        label: "Remove contact",
        icon: <TrashIcon />,
        handler: removeMemberHandler,
        isLoading: isLoading,
        isDisabled: isLoading || !isAllowedEditGenerateReports,
        isHide: false,
      },
    ],
    [
      playbookStatus,
      isLoading,
      isGeneratingPlaybooks,
      isPlaybookLoading,
      isGeneratingPlaybookPDF,
      campaignDetails?.has_hg_data,
      summary?.crm,
    ],
  );

  return (
    <Stack
      style={{
        width: "48px",
        minWidth: "48px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <IconButton
        disabled={isDisabled}
        aria-label="more"
        id="long-button"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon width="24" />
      </IconButton>
      <StyledMenu
        id="demo-customized-menu"
        MenuListProps={{
          "aria-labelledby": "demo-customized-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "white",
          },
        }}
      >
        {list.map(({ label, handler, icon, isDisabled, isLoading, isHide }) => {
          if (isHide) return null;

          return (
            <MenuItem
              key={label}
              disabled={isLoading || isDisabled}
              sx={{
                display: "flex",
                gap: "12px",
                alignItems: "center",
                color: "#1D3E70",
              }}
              onClick={handler}
            >
              {isLoading ? (
                <CircularProgress size={24} sx={{ color: "inherit" }} />
              ) : (
                icon
              )}
              {label}
            </MenuItem>
          );
        })}
      </StyledMenu>

      {isMissingHG && (
        <ReuseModal
          isOpen={isMissingHG}
          onClose={() => setIsMissingHG(false)}
          title={"Missing HG File"}
          icon={"alert_in_empty_triangle"}
          maxWidth={"430px"}
          onSubmit={generatePlaybooksHandler}
          submitBtnColor={"#1C3C6C"}
          submitBtnText={"Continue"}
        >
          <Typography
            component="span"
            variant={"subtitle1"}
            sx={{
              color: "#464646",
              paddingBottom: "16px",
            }}
          >
            HG file is missing for this account. Some sections may be
            unavailable. Add it via resync.
          </Typography>
        </ReuseModal>
      )}
    </Stack>
  );
};
